import React, { useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDeleteGpuServer } from '../../../hooks/useDeleteGpuServer';
import type { GpuServer } from '../../../types/models/GpuServer';

interface DeleteGpuServerModalProps {
  server: GpuServer;
  onClose: () => void;
  /** The server as the DELETE returned it (normally `deleting`, sometimes already `deleted`). */
  onDeleted: (server: GpuServer) => void;
}

/**
 * Confirmation for `DELETE /datacenters/gpu-servers/{id}`. Deleting erases the server and its data,
 * so the hostname is typed out in full (trimmed, case-insensitive). The current, already-paid hour is
 * not refunded, and the disk is kept 96 h for a support restore only — the copy says exactly that,
 * nothing more. A refusal (still starting, impersonation, …) is shown verbatim, inline.
 *
 * Mount it only while open, so the typed confirmation and the error reset on every opening.
 */
const DeleteGpuServerModal: React.FunctionComponent<DeleteGpuServerModalProps> = ({ server, onClose, onDeleted }) => {
  const { addNotification, setDrawerOpen } = useNotifications();
  const { remove, loading: deleting } = useDeleteGpuServer();
  const [confirmName, setConfirmName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const confirmTouched = confirmName.length > 0;
  const confirmMatches = confirmName.trim().toLowerCase() === server.hostname.trim().toLowerCase();

  const close = () => {
    if (!deleting) {
      onClose();
    }
  };

  const handleDelete = async () => {
    setError(null);
    const outcome = await remove(server.id);
    if (outcome.status === 'success') {
      const gone = outcome.server.status === 'deleted';
      addNotification({
        title: gone ? `${server.hostname} deleted` : `${server.hostname} is being deleted`,
        srTitle: 'Success alert',
        // The 96 h trash is promised only for a deletion the customer requested: a server that was
        // already being deleted for another reason keeps its own `destroy_reason`.
        description:
          outcome.server.destroy_reason === 'customer'
            ? 'No further hour will be charged. Its disk is kept for 96 hours: contact support within that time if you need it back.'
            : 'No further hour will be charged.',
        variant: 'success',
      });
      setDrawerOpen(true);
      onDeleted(outcome.server);
      onClose();
      return;
    }
    setError(outcome.message);
    addNotification({
      title: `Could not delete ${server.hostname}`,
      srTitle: 'Error alert',
      description: outcome.message,
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  return (
    <Modal variant="small" isOpen onClose={close} aria-labelledby="delete-gpu-server-title">
      <ModalHeader title="Delete server" titleIconVariant="warning" labelId="delete-gpu-server-title" />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title="The server was not deleted" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Content component="p">
          Deleting <strong>{server.hostname}</strong> stops billing and erases the server. The current hour is not
          refunded. Its disk is kept for 96 hours: contact support within that time if you need it back. After that it
          is permanently erased.
        </Content>
        <Form
          className="pf-v6-u-mt-md"
          onSubmit={(event) => {
            event.preventDefault();
            if (confirmMatches && !deleting) {
              void handleDelete();
            }
          }}
        >
          <FormGroup label={`Type ${server.hostname} to confirm`} fieldId="delete-gpu-server-confirm" isRequired>
            <TextInput
              id="delete-gpu-server-confirm"
              name="delete-gpu-server-confirm"
              type="text"
              value={confirmName}
              onChange={(_event, value) => setConfirmName(value)}
              validated={!confirmTouched ? 'default' : confirmMatches ? 'success' : 'error'}
              isRequired
              isDisabled={deleting}
              autoComplete="off"
              aria-describedby="delete-gpu-server-confirm-helper"
            />
            <FormHelperText>
              <HelperText isLiveRegion>
                <HelperTextItem
                  id="delete-gpu-server-confirm-helper"
                  variant={!confirmTouched ? 'default' : confirmMatches ? 'success' : 'error'}
                >
                  {!confirmTouched
                    ? 'Deletion cannot be undone, so the hostname is typed out in full.'
                    : confirmMatches
                      ? 'The hostname matches.'
                      : `That is not ${server.hostname}.`}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="danger"
          onClick={() => void handleDelete()}
          isLoading={deleting}
          isDisabled={!confirmMatches || deleting}
        >
          Delete server
        </Button>
        <Button variant="link" onClick={close} isDisabled={deleting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DeleteGpuServerModal;
